import { useState, useEffect } from 'react'

import { debounce } from 'lodash'

const EVENT = 'scroll'
const DELAY_OVERFLOW = 1000
const DELAY_POSITION = 200

function useScrollBox({ ref }) {
  const [hasOverflowVertical, setOverflowVertical] = useState(false)
  const [hasOverflowHorizontal, setOverflowHorizontal] = useState(false)
  const [canScrollUp, setScrollUp] = useState(false)
  const [canScrollDown, setScrollDown] = useState(false)
  const [canScrollRight, setScrollRight] = useState(false)
  const [canScrollLeft, setScrollLeft] = useState(false)

  const initScroll = () => {
    ref.current.scrollTo(0, 0)
  }

  const scrollHorizontal = (distance) => {
    ref.current.scrollBy({ left: distance, behavior: 'smooth' })
    refreshOverflow()
    refreshPosition()
  }

  const scrollVertical = (distance) => {
    ref.current.scrollBy({ top: distance, behavior: 'smooth' })
    refreshOverflow()
    refreshPosition()
  }

  const scrollLeft = (distance) => {
    scrollHorizontal(-distance)
  }

  const scrollRight = (distance) => {
    scrollHorizontal(distance)
  }

  const scrollUp = (distance) => {
    scrollVertical(-distance)
  }

  const scrollDown = (distance) => {
    scrollVertical(distance)
  }

  const refreshPosition = () => {
    const { scrollTop, scrollLeft, scrollWidth, scrollHeight, clientHeight, clientWidth } = ref.current

    // 6 Oct 2020 support only firefox browser
    // setScrollDown(scrollTop !== scrollTopMax)
    // setScrollRight(scrollLeft !== scrollLeftMax)
    setScrollDown(scrollTop !== scrollHeight - clientHeight)
    setScrollUp(scrollTop > 0)
    setScrollRight(scrollLeft !== scrollWidth - clientWidth)
    setScrollLeft(scrollLeft > 0)
  }

  const refreshOverflow = () => {
    const { scrollHeight, clientHeight, scrollWidth, clientWidth } = ref.current

    const hasOverflowVertical = scrollHeight > clientHeight
    const hasOverflowHorizontal = scrollWidth > clientWidth

    setOverflowVertical(hasOverflowVertical)
    setOverflowHorizontal(hasOverflowHorizontal)
  }

  const debounceRefreshOverflow = debounce(refreshOverflow, DELAY_OVERFLOW)
  const debounceRefreshPosition = debounce(refreshPosition, DELAY_POSITION)

  useEffect(() => {
    initScroll()
    refreshOverflow()
    refreshPosition()

    ref.current.addEventListener(EVENT, debounceRefreshPosition)
    return () => {
      ref.current.removeEventListener(EVENT, debounceRefreshPosition)
      debounceRefreshOverflow.cancel()
    }
  }, [])

  return {
    scrollUp,
    scrollDown,
    scrollLeft,
    scrollRight,
    refreshOverflow,
    refreshPosition,
    canScrollUp,
    canScrollDown,
    canScrollLeft,
    canScrollRight,
    hasOverflowVertical,
    hasOverflowHorizontal,
  }
}

export default useScrollBox
